import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  alpha,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  Alert
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  GetApp as GetAppIcon,
  Search as SearchIcon
} from '@mui/icons-material';

const TabelaDetalhada = ({ dados, customTheme }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('senha');
  const [order, setOrder] = useState('asc');

  // Usar dados reais se disponíveis
  const dadosTabela = dados?.senhasDetalhadas || [];

  // Verificar se temos dados
  if (!dados || !dadosTabela.length) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        Nenhum dado detalhado disponível para o período selecionado.
      </Alert>
    );
  }

  // Filtrar dados baseado na pesquisa
  const filteredData = dadosTabela.filter(row =>
    Object.values(row).some(value =>
      value.toString().toLowerCase().includes(search.toLowerCase())
    )
  );

  // Ordenação
  const sortedData = filteredData.sort((a, b) => {
    if (order === 'asc') {
      return a[orderBy] < b[orderBy] ? -1 : 1;
    }
    return a[orderBy] > b[orderBy] ? -1 : 1;
  });

  // Paginação
  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getStatusColor = (status) => {
    const cores = {
      'Concluído': '#4caf50',
      'Em Andamento': '#ff9800',
      'Cancelado': '#f44336'
    };
    return cores[status];
  };

  const getTempoEsperaColor = (tempo) => {
    const minutos = parseInt(tempo.split(':')[0]);
    if (minutos <= 10) return '#4caf50';
    if (minutos <= 20) return '#ff9800';
    return '#f44336';
  };

  return (
    <Paper
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Relatório Detalhado de Atendimentos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Visualização detalhada de todos os atendimentos realizados no período selecionado
        </Typography>

        {/* Barra de ferramentas */}
        <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
          <TextField
            placeholder="Pesquisar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<GetAppIcon />}
            onClick={() => console.log('Exportar relatório completo')}
            sx={{ borderRadius: 2 }}
          >
            Exportar Excel
          </Button>
        </Stack>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(customTheme.palette.primary.main, 0.05) }}>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'senha'}
                  direction={orderBy === 'senha' ? order : 'asc'}
                  onClick={() => handleSort('senha')}
                  sx={{ fontWeight: 600 }}
                >
                  Senha
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'servico'}
                  direction={orderBy === 'servico' ? order : 'asc'}
                  onClick={() => handleSort('servico')}
                  sx={{ fontWeight: 600 }}
                >
                  Serviço
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'dataHora'}
                  direction={orderBy === 'dataHora' ? order : 'asc'}
                  onClick={() => handleSort('dataHora')}
                  sx={{ fontWeight: 600 }}
                >
                  Data/Hora
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tempo Espera</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tempo Atendimento</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Atendente</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Observações</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  '&:hover': {
                    bgcolor: alpha(customTheme.palette.primary.main, 0.02),
                  },
                }}
              >
                <TableCell>
                  <Chip
                    label={row.senha}
                    size="small"
                    sx={{
                      bgcolor: alpha(customTheme.palette.primary.main, 0.1),
                      color: customTheme.palette.primary.main,
                      fontWeight: 600
                    }}
                  />
                </TableCell>
                <TableCell>{row.servico}</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {new Date(row.dataHora).toLocaleDateString('pt-BR')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(row.dataHora).toLocaleTimeString('pt-BR')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.tempoEspera}
                    size="small"
                    sx={{
                      bgcolor: alpha(getTempoEsperaColor(row.tempoEspera), 0.1),
                      color: getTempoEsperaColor(row.tempoEspera),
                      fontWeight: 600
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {row.tempoAtendimento}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.status}
                    size="small"
                    sx={{
                      bgcolor: alpha(getStatusColor(row.status), 0.1),
                      color: getStatusColor(row.status),
                      fontWeight: 600
                    }}
                  />
                </TableCell>
                <TableCell>{row.atendente}</TableCell>
                <TableCell>
                  <Tooltip title={row.observacoes} placement="top">
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 150
                      }}
                    >
                      {row.observacoes}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="Ver detalhes">
                    <IconButton
                      size="small"
                      onClick={() => console.log('Ver detalhes:', row)}
                      sx={{ color: customTheme.palette.primary.main }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
        sx={{
          borderTop: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`,
          bgcolor: alpha(customTheme.palette.primary.main, 0.02),
        }}
      />
    </Paper>
  );
};

export default TabelaDetalhada; 